import React from 'react';
import { Input, InputProps } from '@mui/material';
import cx from 'classnames';
import styled from 'styled-components';
import FormLabel from '../FormLabel';
import FormHelperText from '../FormHelperText';

const StyledTextField = styled.div`
    width: 100%;
    & .input-label {
        margin-bottom: 0.125rem;
    }
    & .text-field {
        border: 1px solid var(--black-30, rgba(17, 17, 17, 0.3));
        border-radius: 0.375rem;
    }
    & .required-label {
        color: #ff0000;
    }
    & .text-field input {
        letter-spacing: 0.4px;
        color: #111111;
        padding: 0.8rem 0.8rem;
        font-size: 1rem;
    }
    & input:read-only {
        cursor: no-drop;
    }
    /* Chrome autofill input small font workaround
             https://stackoverflow.com/questions/57242841/input-text-very-small-when-hovering-on-autofill-suggestion */
    & input:-webkit-autofill::first-line {
        font-size: 1rem;
    }

    &.has-error .text-field {
        border: 2px solid #df352c;
    }
    & .text-field:hover {
        border: 1px solid rgba(17, 17, 17, 0.6);
    }

    & .text-field.focused {
        border: 1px solid rgba(17, 17, 17, 0.6);
    }

    & .error-text {
        height: 21px;
        visibility: hidden;
    }
    & .error-text.visible {
        visibility: visible;
    }
    &.read-only .text-field,
    &.read-only .text-field:hover {
        border: 2px solid #c2cbd4 !important;
        cursor: no-drop;
    }
    &.has-error .text-field:hover,
    &.has-error.read-only .text-field,
    &.has-error.read-only .text-field:hover {
        border: 2px solid #df352c !important;
    }
    &.has-error .text-field.focused {
        border: 2px solid #df352c !important;
    }

    &.MuiInputBase-root {
        box-sizing: border-box;
    }
    .MuiInputBase-input {
        height: 1.1876em;
    }
`;

export interface TextFieldProps extends InputProps {
    className?: string;
    label?: string | React.ReactNode;
    helperText?: string;
    onChange?:
        | ((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void)
        | undefined;
    helperTextProps?: any;
    [props: string]: any;
}

/**
 *
 * Custom TextField component implementation for Convo Link UI
 */
const TextField: React.FC<TextFieldProps> = ({
    className,
    label,
    error,
    helperText,
    helperTextProps,
    readOnly,
    required,
    ...props
}) => {
    return (
        <StyledTextField className={cx(className, { 'has-error': !!error, 'read-only': readOnly })}>
            {label && (
                <FormLabel className="input-label">
                    {label}
                    {required && <span className="required-label">*</span>}
                </FormLabel>
            )}
            <Input
                {...props}
                disableUnderline
                fullWidth
                classes={{
                    root: 'text-field',
                    disabled: 'disabled',
                    focused: 'focused',
                }}
                readOnly={readOnly}
            />
            <FormHelperText className={cx('error-text', { visible: !!error })} {...helperTextProps}>
                {helperText}
            </FormHelperText>
        </StyledTextField>
    );
};

export default TextField;
